import { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import usePracticeSet from '@/api/usePracticeSet';
import useSubmitPracticeSet from '@/api/useSubmitPracticeSet';
import useGenerateHint from '@/api/useGenerateHint';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { Modal } from 'react-daisyui';
import ReadingInsertButton from '@/components/ui/ReadingInsertButton/ReadingInsertButton';
import ReadingInsertModal from '@/components/ui/ReadingInsertModal/ReadingInsertModal';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import ModelAnswerButton from '@/components/ui/ModelAnswerButton/ModelAnswerButton';
import TipButton from '@/components/ui/TipButton/TipButton';
import useCategoryTips from '@/api/useCategoryTips';
import ExitPracticeSetButton from '@/components/ui/ExitPracticeSetButton/ExitPracticeSetButton';
import supabaseClient from '@/api/supabaseClient.ts';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import QuestionContentArea from '@/components/ui/QuestionContentArea/QuestionContentArea';

import 'swiper/css';
import 'swiper/css/navigation';
import { Document, Page } from 'react-pdf';
import { pdfOptions } from '@/utils/pdfConfig';

interface ParsedQuestionContent {
  hasExtract: boolean;
  questionNumber: string;
  extractInstruction: string;
  extractText: string;
  mainQuestion: string;
  referencedLines: { start: number; end: number } | null;
}

const TakePracticeSetPage: FC = () => {
  const { practiceSetId } = useParams<{ practiceSetId: string }>();
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const swiperRef = useRef<SwiperType | null>(null);
  const queryClient = useQueryClient();

  const { data: practiceSet, isLoading } = usePracticeSet(practiceSetId);
  const submitPracticeSet = useSubmitPracticeSet();
  const generateHint = useGenerateHint();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedSectionBQuestion, setSelectedSectionBQuestion] = useState<string | null>(null);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showReadingInsert, setShowReadingInsert] = useState(false);
  const [showReadingInsert2, setShowReadingInsert2] = useState(false);
  const [isQuestionCollapsed, setIsQuestionCollapsed] = useState(false);
  const [generatedHint, setGeneratedHint] = useState<string | null>(null);

  // redirect to results if graded
  // Redirect to results if attempt is already submitted
  useEffect(() => {
    if (practiceSet && practiceSet.status !== 'in_progress') {
      navigate(`/practice-sets/${practiceSetId}/results`, { replace: true });
    }
  }, [practiceSet, practiceSetId, navigate]);

  // Auto-save timeout ref (using ref instead of state to avoid re-renders)
  const saveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Initialize answers from practice set
  useEffect(() => {
    if (practiceSet?.practice_answers) {
      const initialAnswers: Record<string, string> = {};
      let sectionBAnswerId: string | null = null;

      practiceSet.practice_answers.forEach((answer) => {
        if (answer?.answer_text) {
          initialAnswers[answer.id] = answer.answer_text;

          // Check if this is a Section B question with an answer
          if (answer.questions?.category === 'SectionB') {
            sectionBAnswerId = answer.id;
          }
        }
      });

      setAnswers(initialAnswers);
      if (sectionBAnswerId) {
        setSelectedSectionBQuestion(sectionBAnswerId);
      }
    }
  }, [practiceSet]);

  // Check if practice set has both Section B questions (Mock Past Paper mode)
  const sectionBQuestions = useMemo(() => {
    if (!practiceSet?.practice_answers) return [];
    return practiceSet.practice_answers.filter((a) => {
      const category = a.questions?.category;
      return category === 'SectionB';
    });
  }, [practiceSet]);

  const hasBothSectionB = sectionBQuestions.length === 2;

  // Calculate total slides (account for Section B being combined into one slide)
  const totalSlides = hasBothSectionB
    ? practiceSet?.practice_answers.length
      ? practiceSet.practice_answers.length - 1
      : 0
    : practiceSet?.practice_answers.length || 0;

  const currentPracticeAnswer = useMemo(() => {
    if (!practiceSet?.practice_answers) return undefined;

    // If on Section B slide in mock past paper, return the selected Section B answer
    if (hasBothSectionB && currentQuestionIndex === totalSlides - 1 && selectedSectionBQuestion) {
      return practiceSet.practice_answers.find((a) => a.id === selectedSectionBQuestion);
    }

    // Otherwise, return the current answer (excluding Section B if mock past paper)
    const filteredAnswers = practiceSet.practice_answers.filter(
      (a) => !hasBothSectionB || a.questions?.category !== 'SectionB'
    );
    return filteredAnswers[currentQuestionIndex];
  }, [practiceSet, currentQuestionIndex, hasBothSectionB, selectedSectionBQuestion, totalSlides]);

  // Get tips for current question category from database
  const currentCategoryCode = currentPracticeAnswer?.questions?.category_code || undefined;
  const { data: categoryTips = [] } = useCategoryTips(currentCategoryCode);

  // Parse question content to detect extracts and line references
  const parseQuestionContent = useCallback((content: string): ParsedQuestionContent => {
    if (!content) {
      return {
        hasExtract: false,
        questionNumber: '',
        extractInstruction: '',
        extractText: '',
        mainQuestion: '',
        referencedLines: null,
      };
    }

    // Check for line references (e.g., "lines 1-3", "from lines 7 to 17", "line 5")
    const lineReferencePatterns = [
      /(?:from\s+)?lines?\s+(\d+)[-–]\s*(\d+)/i, // "lines 1-3" or "from lines 7-17"
      /(?:from\s+)?lines?\s+(\d+)\s+to\s+(\d+)/i, // "lines 1 to 3"
      /line\s+(\d+)/i, // "line 5" (single line)
    ];

    let referencedLines: { start: number; end: number } | null = null;
    for (const pattern of lineReferencePatterns) {
      const match = content.match(pattern);
      if (match) {
        const start = parseInt(match[1], 10);
        const end = match[2] ? parseInt(match[2], 10) : start; // Single line if no end specified
        referencedLines = { start, end };
        break;
      }
    }

    // Check for extract markers added by OpenAI
    const extractStartMarker = '<<<EXTRACT_START>>>';
    const extractEndMarker = '<<<EXTRACT_END>>>';

    if (content.includes(extractStartMarker) && content.includes(extractEndMarker)) {
      const startIndex = content.indexOf(extractStartMarker);
      const endIndex = content.indexOf(extractEndMarker);

      // Extract the parts
      const beforeExtract = content.substring(0, startIndex).trim();
      const extractText = content.substring(startIndex + extractStartMarker.length, endIndex).trim();
      const afterExtract = content.substring(endIndex + extractEndMarker.length).trim();

      // Parse question number and instruction from the first line
      const firstLine = beforeExtract.split('\n')[0] || '';
      const match = firstLine.match(/^(\d+)\s+(.*)/);

      return {
        hasExtract: true,
        questionNumber: match ? match[1] : '',
        extractInstruction: beforeExtract,
        extractText: extractText,
        mainQuestion: afterExtract,
        referencedLines,
      };
    }

    // If no extract markers found, return the whole content as main question
    return {
      hasExtract: false,
      questionNumber: '',
      extractInstruction: '',
      extractText: '',
      mainQuestion: content,
      referencedLines,
    };
  }, []);

  // Save answer to database (direct upsert)
  const saveAnswer = useCallback(
    async (answerId: string, answerText: string) => {
      if (!session?.user?.id) return;

      try {
        // Direct update to database using the practice_answer ID
        await supabaseClient
          .from('practice_answers')
          .update({
            answer_text: answerText,
            updated_at: new Date().toISOString(),
          })
          .eq('id', answerId)
          .eq('auth_id', session.user.id);

        console.log(`Saved answer ${answerId}`);
      } catch (error) {
        console.error('Failed to save answer:', error);
      }
    },
    [session]
  );

  const handleAnswerChange = useCallback(
    (questionId: string, value: string) => {
      // Update local state immediately
      setAnswers((prev) => ({ ...prev, [questionId]: value }));

      // Clear existing timeout for this question
      if (saveTimeoutRef.current[questionId]) {
        clearTimeout(saveTimeoutRef.current[questionId]);
      }

      // Set new timeout to auto-save after 1 second
      saveTimeoutRef.current[questionId] = setTimeout(() => {
        saveAnswer(questionId, value);
      }, 1000);
    },
    [saveAnswer]
  );

  const handleNext = useCallback(() => {
    // Save current answer immediately before navigating
    if (currentPracticeAnswer) {
      const answerText = answers[currentPracticeAnswer.id] || '';

      // Clear any pending timeout
      if (saveTimeoutRef.current[currentPracticeAnswer.id]) {
        clearTimeout(saveTimeoutRef.current[currentPracticeAnswer.id]);
        delete saveTimeoutRef.current[currentPracticeAnswer.id];
      }

      // Save immediately
      saveAnswer(currentPracticeAnswer?.id, answerText);
    }

    // Reset question collapsed state when changing questions
    setIsQuestionCollapsed(false);

    // Use swiper to navigate
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  }, [currentPracticeAnswer, answers, saveAnswer]);

  const handleSlideChange = useCallback(() => {
    if (swiperRef.current) {
      setCurrentQuestionIndex(swiperRef.current.activeIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Reset generated content when changing questions
      setGeneratedHint(null);
    }
  }, []);

  const handleBack = useCallback(() => {
    if (currentQuestionIndex > 0 && swiperRef.current) {
      swiperRef.current.slidePrev();
    }
    // Do nothing on first question (button is disabled)
  }, [currentQuestionIndex]);

  const handleViewHint = useCallback(async () => {
    if (!currentPracticeAnswer) return;

    setShowHintModal(true);

    // If hint already exists in the questions table, no need to generate
    if (currentPracticeAnswer.questions?.hint_content) {
      setGeneratedHint(currentPracticeAnswer.questions.hint_content);
      return;
    }

    // If we've already generated a hint for this question in this session
    if (generatedHint) {
      return;
    }

    // Generate hint using AI
    try {
      const result = await generateHint.mutateAsync({
        practiceAnswerId: currentPracticeAnswer.id,
      });

      setGeneratedHint(result.hint);

      // Invalidate cache to refresh the practice set data
      queryClient.invalidateQueries({ queryKey: ['practice_set', practiceSetId] });
    } catch (error) {
      console.error('Failed to generate hint:', error);
    }
  }, [currentPracticeAnswer, generatedHint, generateHint, practiceSetId, queryClient]);

  const handleSubmit = useCallback(async () => {
    if (!practiceSetId) return;

    // Clear all pending save timeouts and save immediately
    const savePromises = Object.entries(saveTimeoutRef.current).map(([questionId, timeout]) => {
      clearTimeout(timeout);
      delete saveTimeoutRef.current[questionId];
      const answerText = answers[questionId] || '';
      if (answerText) {
        return saveAnswer(questionId, answerText);
      }
      return Promise.resolve();
    });

    await Promise.all(savePromises);

    // For mock past papers, ensure only the selected Section B answer is kept
    if (hasBothSectionB && selectedSectionBQuestion && session?.user?.id) {
      const nonSelectedSectionB = sectionBQuestions.find((a) => a.id !== selectedSectionBQuestion);
      if (nonSelectedSectionB) {
        // Clear the non-selected Section B answer
        await supabaseClient
          .from('practice_answers')
          .update({ answer_text: '' })
          .eq('id', nonSelectedSectionB.id)
          .eq('auth_id', session.user.id);
      }
    }

    // Subscribe to status changes using Realtime
    const subscription = supabaseClient
      .channel(`practice-set-status-${practiceSetId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'practice_attempts',
          filter: `id=eq.${practiceSetId}`,
        },
        (payload) => {
          const newStatus = (payload.new as { status: string }).status;
          if (newStatus === 'graded') {
            subscription.unsubscribe();
            setShowSubmitModal(false);
            navigate(`/practice-sets/${practiceSetId}/results`);
          }
        }
      )
      .subscribe();

    // Trigger submission (background)
    submitPracticeSet.mutate({ practiceSetId });

    // Safety timeout - 30 seconds (grading may take longer than papers)
    setTimeout(() => {
      subscription.unsubscribe();
      setShowSubmitModal(false);
      navigate(`/practice-sets/${practiceSetId}/results`);
    }, 5000);
  }, [
    practiceSetId,
    answers,
    saveAnswer,
    submitPracticeSet,
    navigate,
    hasBothSectionB,
    sectionBQuestions,
    selectedSectionBQuestion,
    session?.user.id,
  ]);

  if (isLoading) {
    return (
      <>
        <PageHeader title="Loading..." />
        <div className="flex items-center justify-center flex-1">
          <p className="text-gray-500">Loading practice set...</p>
        </div>
      </>
    );
  }

  if (!practiceSet) {
    return (
      <>
        <PageHeader title="Practice Set Not Found" />
        <div className="flex items-center justify-center flex-1">
          <p className="text-gray-500">Practice set not found.</p>
        </div>
      </>
    );
  }

  const progress = ((currentQuestionIndex + 1) / totalSlides) * 100;

  // Get current question category for title
  const getCurrentQuestionTitle = () => {
    // If on Section B slide in mock past paper, show "Section B"
    if (hasBothSectionB && currentQuestionIndex === totalSlides - 1) {
      return 'Section B';
    }
    // Otherwise show the current question's category
    const currentAnswer = practiceSet.practice_answers.filter(
      (a) => !hasBothSectionB || a.questions?.category !== 'SectionB'
    )[currentQuestionIndex];
    const category = currentAnswer?.questions?.category || practiceSet.question_category;

    // Format category: "SectionB" -> "Section B"
    return category.replace(/SectionB/g, 'Section B').replace(/_/g, ' - ');
  };

  return (
    <>
      <PageHeader
        title={`${getCurrentQuestionTitle()} - Question ${currentQuestionIndex + 1}/${totalSlides}`}
        showBackButton
        onBackClick={handleBack}
        disableBackButton={currentQuestionIndex === 0}
        rightContent={practiceSetId && <ExitPracticeSetButton practiceSetId={practiceSetId} />}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col gap-6 overflow-hidden">
        {/* Progress Bar */}
        <div className="w-full h-2 bg-gray-200 rounded-full flex-shrink-0">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Question and Answer Container with Swiper */}
        <Swiper
          modules={[Navigation]}
          spaceBetween={0}
          slidesPerView={1}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={handleSlideChange}
          allowTouchMove={false}
          className="flex-1 w-full"
        >
          {/* Regular questions (excluding Section B if mock past paper) */}
          {practiceSet.practice_answers
            .filter((a) => !hasBothSectionB || a.questions?.category !== 'SectionB')
            .map((answer) => {
              const qData = answer.questions;
              const questionPdfUrl = qData?.pdf_url
                ? supabaseClient.storage.from('past-papers').getPublicUrl(qData.pdf_url).data.publicUrl
                : '';

              return (
                <SwiperSlide key={answer.id} className="h-full overflow-y-auto">
                  <div className="flex flex-col gap-6 p-1 min-h-full">
                    <QuestionContentArea
                      questionContent={qData?.content || ''}
                      marks={qData?.marks || 0}
                      imagePageNumbers={qData?.image_page_numbers}
                      pdfUrl={questionPdfUrl}
                      readingInsertContent={qData?.reading_insert_content || undefined}
                      isCollapsed={isQuestionCollapsed}
                      onToggleCollapse={() => setIsQuestionCollapsed(!isQuestionCollapsed)}
                      parseQuestionContent={parseQuestionContent}
                    />

                    {/* Answer Input */}
                    <div className="flex-1 flex flex-col gap-2 pb-4">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">Your Answer:</label>
                        <span className="text-xs text-gray-500">
                          {(answers[answer.id] || '').length} / 4,500 characters
                        </span>
                      </div>
                      {(answers[answer.id] || '').length >= 4500 && (
                        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-sm text-yellow-800">
                          <strong>Character limit exceeded</strong> — shorten your response while keeping key points to
                          save exam time
                        </div>
                      )}
                      <textarea
                        value={answers[answer.id] || ''}
                        onChange={(e) => handleAnswerChange(answer.id, e.target.value)}
                        placeholder="Type your answer here..."
                        className="flex-1 w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 resize-none"
                        maxLength={4500}
                      />
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}

          {/* Section B selection slide (only for mock past papers) */}
          {hasBothSectionB && (
            <SwiperSlide className="h-full overflow-y-auto">
              <div className="flex flex-col gap-6 p-1 min-h-full">
                {/* Section B Choice */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden transition-all flex-shrink-0">
                  <div
                    className={`p-6 ${isQuestionCollapsed ? '' : 'max-h-[60vh] overflow-y-auto'} flex flex-col gap-6 relative`}
                  >
                    {/* Collapse/Expand Button */}
                    <button
                      onClick={() => setIsQuestionCollapsed(!isQuestionCollapsed)}
                      className="absolute top-4 right-4 w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                      title={isQuestionCollapsed ? 'Expand questions' : 'Collapse questions'}
                    >
                      {isQuestionCollapsed ? (
                        <ChevronDownIcon className="w-10 h-10" />
                      ) : (
                        <ChevronUpIcon className="w-10 h-10" />
                      )}
                    </button>

                    {/* Question Text */}
                    <div className="flex flex-col gap-4 pr-14">
                      {isQuestionCollapsed ? (
                        <p className="text-base leading-relaxed whitespace-pre-wrap line-clamp-2">
                          Choose ONE of the following questions to answer
                        </p>
                      ) : (
                        <>
                          <p className="text-base font-semibold text-gray-700 mb-4">
                            Choose ONE of the following questions to answer:
                          </p>

                          {sectionBQuestions.map((answer) => {
                            const qData = answer.questions;
                            const questionType = qData?.has_image === true ? 'Image' : 'Narrative';
                            const questionPdfUrl = qData?.pdf_url
                              ? supabaseClient.storage.from('past-papers').getPublicUrl(qData.pdf_url).data.publicUrl
                              : '';

                            return (
                              <div
                                key={answer.id}
                                className={`border-2 rounded-lg p-6 transition-all cursor-pointer ${
                                  selectedSectionBQuestion === answer.id
                                    ? 'border-orange-500 bg-orange-50'
                                    : 'border-gray-300 hover:border-orange-300'
                                }`}
                                onClick={() => setSelectedSectionBQuestion(answer.id)}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="flex-shrink-0 mt-1">
                                    <div
                                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                        selectedSectionBQuestion === answer.id
                                          ? 'border-orange-500 bg-orange-500'
                                          : 'border-gray-400'
                                      }`}
                                    >
                                      {selectedSectionBQuestion === answer.id && (
                                        <div className="w-2 h-2 bg-white rounded-full" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-2">
                                      Section B - {questionType} [{qData?.marks || 40} marks]
                                    </h4>
                                    <p className="text-gray-700 text-sm whitespace-pre-wrap mb-4">
                                      {qData?.content || ''}
                                    </p>

                                    {/* Display images if question has them (Section B Image) */}
                                    {qData?.image_page_numbers &&
                                      qData.image_page_numbers.length > 0 &&
                                      questionPdfUrl && (
                                        <div className="flex flex-col gap-2 mt-4">
                                          <p className="text-sm font-medium text-gray-600">Reference Image(s):</p>
                                          <Document file={questionPdfUrl} options={pdfOptions}>
                                            {qData.image_page_numbers.map((pageNum: number) => (
                                              <div key={pageNum} className="mb-4">
                                                <p className="text-xs text-gray-500 mb-2">Page {pageNum}</p>
                                                <Page
                                                  pageNumber={pageNum}
                                                  width={Math.min(window.innerWidth * 0.5, 500)}
                                                  renderTextLayer={false}
                                                  renderAnnotationLayer={false}
                                                  className="shadow-md"
                                                />
                                              </div>
                                            ))}
                                          </Document>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Answer Input for selected Section B question */}
                {selectedSectionBQuestion ? (
                  <div className="flex-1 flex flex-col gap-2 pb-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Your Answer:</label>
                      <span className="text-xs text-gray-500">
                        {(answers[selectedSectionBQuestion] || '').length} / 4,500 characters
                      </span>
                    </div>
                    {(answers[selectedSectionBQuestion] || '').length >= 4500 && (
                      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-sm text-yellow-800">
                        <strong>Character limit exceeded</strong> — shorten your response while keeping key points to
                        save exam time
                      </div>
                    )}
                    <textarea
                      value={answers[selectedSectionBQuestion] || ''}
                      onChange={(e) => handleAnswerChange(selectedSectionBQuestion, e.target.value)}
                      placeholder="Type your answer here..."
                      className="flex-1 w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 resize-none"
                      maxLength={4500}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12">
                    <p className="text-gray-500 text-center px-6">
                      Please select one of the questions above to start writing your answer
                    </p>
                  </div>
                )}
              </div>
            </SwiperSlide>
          )}
        </Swiper>

        {/* Buttons Container */}
        <div className="flex-shrink-0 flex flex-col gap-4">
          {/* Helper Buttons Row - Only show when a question is available */}
          {currentPracticeAnswer && (
            <div className="flex gap-3">
              {/* View Reading Insert - Only show if question has reading insert content */}
              {currentPracticeAnswer.questions?.reading_insert_content && (
                <ReadingInsertButton
                  onClick={() => setShowReadingInsert(true)}
                  label={
                    currentPracticeAnswer.questions?.reading_insert_content_2
                      ? 'Reading Insert Text 1'
                      : 'View Reading Insert'
                  }
                  variant="primary"
                  size="md"
                  className="px-6 py-3 rounded-xl"
                />
              )}
              {currentPracticeAnswer.questions?.reading_insert_content_2 && (
                <ReadingInsertButton
                  onClick={() => setShowReadingInsert2(true)}
                  label="Reading Insert 2"
                  variant="primary"
                  size="md"
                  className="px-6 py-3 rounded-xl"
                />
              )}
              <button
                onClick={handleViewHint}
                className="bg-primary hover:bg-brand-hover text-white px-6 py-3 rounded-xl font-medium transition-colors"
              >
                Hint
              </button>
              {currentPracticeAnswer?.questions?.id && (
                <ModelAnswerButton
                  questionId={currentPracticeAnswer.questions.id}
                  existingModelAnswer={currentPracticeAnswer.questions?.model_answer}
                  practiceSetId={practiceSetId}
                  questionCategory={currentPracticeAnswer.questions?.category || undefined}
                  pdfUrl={currentPracticeAnswer.questions?.pdf_url || undefined}
                  imagePageNumbers={currentPracticeAnswer.questions?.image_page_numbers || undefined}
                />
              )}
              {/* Tips from database */}
              {categoryTips.map((tip, index) => (
                <TipButton key={`${tip.title}-${index}`} tip={tip} />
              ))}
            </div>
          )}

          {/* Next/Submit Buttons */}
          <div className="flex justify-end">
            {currentQuestionIndex === totalSlides - 1 ? (
              <LoadingButton
                loading={false}
                onClick={() => setShowSubmitModal(true)}
                className="bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white px-8 py-3 rounded-xl font-medium"
              >
                Submit Set
              </LoadingButton>
            ) : (
              <button
                onClick={handleNext}
                className="bg-[#2B3440] hover:bg-dark-hover-secondary text-white px-8 py-3 rounded-xl font-medium"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hint Modal */}
      <Modal open={showHintModal} onClose={() => setShowHintModal(false)} className="max-w-2xl">
        <Modal.Header className="font-bold text-xl flex items-center justify-between">
          <span>Hint</span>
          <button
            onClick={() => setShowHintModal(false)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </Modal.Header>
        <Modal.Body>
          {generateHint.isPending ? (
            <div className="flex items-center gap-3 text-gray-600 py-8">
              <svg
                className="animate-spin h-6 w-6 text-orange-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Generating hint...</span>
            </div>
          ) : generateHint.isError ? (
            <p className="text-red-600">Failed to generate hint. Please try again.</p>
          ) : currentPracticeAnswer?.questions?.hint_content || generatedHint ? (
            <div className="space-y-6">
              {(currentPracticeAnswer?.questions?.hint_content || generatedHint || '')
                .split(/\n\n+/)
                .filter((section) => section.trim())
                .map((section, index) => {
                  const lines = section.split('\n').filter((line) => line.trim());
                  if (lines.length === 0) return null;

                  const firstLine = lines[0];

                  // Check if first line is a header (ends with colon, is bold markdown, or is markdown heading)
                  const isHeader =
                    firstLine.trim().endsWith(':') ||
                    /^#+\s/.test(firstLine) ||
                    /^\*\*.*\*\*:?\s*$/.test(firstLine) ||
                    /^-\s*\*\*.*\*\*:/.test(firstLine);

                  if (isHeader) {
                    // Remove markdown formatting from header (**, ##, and leading dashes)
                    let headerText = firstLine
                      .replace(/^-\s*/, '') // Remove leading dash and space
                      .replace(/^#+\s/, '') // Remove markdown heading markers
                      .replace(/\*\*/g, '') // Remove bold markers
                      .replace(/:$/, '') // Remove trailing colon
                      .trim();

                    // Add colon back for display
                    if (!headerText.endsWith(':')) {
                      headerText += ':';
                    }

                    const content = lines.slice(1).join('\n').trim();

                    return (
                      <div key={index} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                        <h3 className="font-semibold text-gray-900 mb-2">{headerText}</h3>
                        <div className="text-gray-700 leading-relaxed whitespace-pre-wrap pl-1">{content}</div>
                      </div>
                    );
                  } else {
                    // No header, just display content
                    return (
                      <div key={index} className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {section}
                      </div>
                    );
                  }
                })
                .filter(Boolean)}
            </div>
          ) : (
            <p className="text-gray-500">No hint available for this question.</p>
          )}
        </Modal.Body>
        <Modal.Actions>
          <button
            onClick={() => setShowHintModal(false)}
            className="bg-[#2B3440] hover:bg-dark-hover-secondary text-white px-8 py-3 rounded-xl font-medium w-full"
            disabled={generateHint.isPending}
          >
            Close
          </button>
        </Modal.Actions>
      </Modal>

      {/* Submit Confirmation Modal */}
      <Modal open={showSubmitModal} onClose={() => setShowSubmitModal(false)} className="max-w-md">
        <Modal.Header className="font-bold text-xl flex items-center justify-between">
          <span>Submit Practice Set?</span>
          <button
            onClick={() => setShowSubmitModal(false)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you ready to submit your practice set? Your answers will be graded and you won't be able to make changes
            after submission.
          </p>
        </Modal.Body>
        <Modal.Actions className="flex gap-3">
          <button
            onClick={() => setShowSubmitModal(false)}
            className="flex-1 bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <LoadingButton
            loading={submitPracticeSet.isPending}
            onClick={handleSubmit}
            className="flex-1 bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white px-6 py-3 rounded-xl font-medium"
          >
            Submit
          </LoadingButton>
        </Modal.Actions>
      </Modal>

      {/* Reading Insert Modal (Text 1 / default) */}
      {(currentPracticeAnswer?.questions?.reading_insert_content ||
        (currentPracticeAnswer?.questions?.reading_insert_page_numbers &&
          currentPracticeAnswer?.questions?.pdf_url)) && (
        <ReadingInsertModal
          isOpen={showReadingInsert}
          onClose={() => setShowReadingInsert(false)}
          title={
            currentPracticeAnswer.questions.reading_insert_content_2 ? 'Reading Insert Text 1' : 'Reading Text Insert'
          }
          textContent={currentPracticeAnswer.questions.reading_insert_content || undefined}
          pdfUrl={
            currentPracticeAnswer.questions.pdf_url
              ? supabaseClient.storage.from('past-papers').getPublicUrl(currentPracticeAnswer.questions.pdf_url).data
                  .publicUrl
              : undefined
          }
          pageNumbers={currentPracticeAnswer.questions.reading_insert_page_numbers || undefined}
          defaultPosition="left"
        />
      )}

      {/* Reading Insert Modal 2 (Text 2) */}
      {currentPracticeAnswer?.questions?.reading_insert_content_2 && (
        <ReadingInsertModal
          isOpen={showReadingInsert2}
          onClose={() => setShowReadingInsert2(false)}
          title="Reading Insert 2"
          textContent={currentPracticeAnswer.questions.reading_insert_content_2}
          defaultPosition="right"
        />
      )}
    </>
  );
};

export default TakePracticeSetPage;
